import { TABLE_LIST_TYPE } from "@constants";
import {
    getSearchParamsAdminUsers,
    getSearchParamsProjectUsers,
    getSearchParamsAllAssets,
    getSearchParamsAsset,
    getSearchParamsAssetV2,
    getSearchParamsAuditLogs,
    getSearchParamsCollection,
    getSearchParamsCustomContent,
    getSearchParamsMetadataExports,
    getSearchParamsAutomatedWorkflow,
    getSearchParamsAutomatedWorkflowExecution,
    getSearchParamsWebhook,
    getSearchParamsMySharedAssets,
    getSearchParamsVocabulary,
    getSearchParamsProjectAssignableGroups,
    getSearchParamsRoleAssignableTeams,
    getSearchParamsRoleAssignableUsers,
    getSearchParamsProjectAssignableUsers,
} from "@features/list-search/search-params";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);
const useSearchFilter = (domain, pageType = "search", filterType, isSearchEngine = false, version) => {
    const getSearchFields = (options, fields, getCustomMetadataOptions) => {
        switch (domain) {
            case TABLE_LIST_TYPE.CUSTOM_CONTENT:
                return getSearchParamsCustomContent(fields, getCustomMetadataOptions);
            case TABLE_LIST_TYPE.ASSET:
                return isSearchEngine ? getSearchParamsAssetV2(version) : getSearchParamsAsset(version);
            case TABLE_LIST_TYPE.COLLECTION:
                return getSearchParamsCollection();
            case TABLE_LIST_TYPE.VOCABULARY:
                return getSearchParamsVocabulary();
            case TABLE_LIST_TYPE.METADATA_EXPORTS:
                return getSearchParamsMetadataExports();
            case TABLE_LIST_TYPE.AUTOMATED_WORKFLOW:
                return getSearchParamsAutomatedWorkflow();
            case TABLE_LIST_TYPE.AUTOMATED_WORKFLOW_EXECUTION:
                return getSearchParamsAutomatedWorkflowExecution();
            case TABLE_LIST_TYPE.WEBHOOK:
                return getSearchParamsWebhook();
            case TABLE_LIST_TYPE.AUDIT_LOGS:
                return getSearchParamsAuditLogs(options?.domain, fields);
            case TABLE_LIST_TYPE.MY_SHARED_ASSETS:
                return getSearchParamsMySharedAssets();
            case TABLE_LIST_TYPE.SEARCH_WITH_SE:
                return getSearchParamsAllAssets();
            case TABLE_LIST_TYPE.ADMIN_USERS:
                return getSearchParamsAdminUsers();
            case TABLE_LIST_TYPE.PROJECT_USERS:
                return getSearchParamsProjectUsers();
            case TABLE_LIST_TYPE.PROJECT_ASSIGNABLE_GROUPS:
                return getSearchParamsProjectAssignableGroups();
            case TABLE_LIST_TYPE.PROJECT_ASSIGNABLE_USERS:
                return getSearchParamsProjectAssignableUsers();
            case TABLE_LIST_TYPE.ROLE_ASSIGNABLE_TEAMS:
                return getSearchParamsRoleAssignableTeams();
            case TABLE_LIST_TYPE.ROLE_ASSIGNABLE_USERS:
                return getSearchParamsRoleAssignableUsers();
            default:
                return [];
        }
    };

    const parseSearchParamsToSearchValue = (params) => {
        const decodedParams = params;

        const searchValues = getSearchFields().reduce((acc, cur) => {
            return cur.attributions ? acc.concat(cur.attributions) : acc;
        }, []);

        const checkValidation = (el) => {
            // NOTE: 목록 페이지에서는 필수 필터만 보여주고, 검색 랜딩 페이지에서는 필수 필터와 선택한 필터 모두 보여준다.
            return pageType === "list" ? el.essential : decodedParams[el.id] || el.essential;
        };

        const checkTypeValidation = (el) => {
            const { type, id, essential } = el;
            if (essential) return true;
            else {
                if (type === "date") return decodedParams[`${id}StartAt`] || decodedParams[`${id}EndAt`];
                if (type === "dateTimeRange") return decodedParams[`${id}From`] || decodedParams[`${id}To`];
                return true;
            }
        };

        const paramsForChips = searchValues
            .filter((el) => checkValidation(el) && checkTypeValidation(el))
            .map(({ id, type, subtitle, multiple, essential, isCustomField, options, placeholder }) => {
                const value = decodedParams[id];
                const name = decodedParams[`${id}Name`];

                if (essential && !decodedParams[id] && type !== "assetDateRange") {
                    return {
                        id: id,
                        title: subtitle,
                        label: name,
                        value: null,
                        type,
                        essential,
                        multiple,
                        length: multiple ? value?.length - 1 : null,
                        placeholder,
                    };
                }

                if (isCustomField)
                    return {
                        id: id,
                        title: subtitle,
                        label: Array.isArray(value) ? value[0] : value,
                        value,
                        type,
                        length: Array.isArray(value) ? value.length : null,
                        essential,
                        isCustomField: true,
                        options,
                        placeholder,
                    };

                if (type === "text")
                    return { id: id, title: subtitle, label: value, value, type, length: null, essential };

                if (type === "users")
                    return {
                        id: id,
                        title: subtitle,
                        label: name,
                        value,
                        type,
                        length: null,
                        essential,
                        placeholder,
                    };

                if (type === "owners")
                    return {
                        id: id,
                        title: subtitle,
                        label: value?.name,
                        value,
                        type,
                        length: null,
                        essential,
                        placeholder,
                    };
                if (type === "assetDateRange") {
                    let period = "";
                    let startDate = decodedParams.startAt && dayjs(decodedParams.startAt).format("YYYY-MM-DD");
                    let endDate = decodedParams.endAt && dayjs(decodedParams.endAt).format("YYYY-MM-DD");

                    if (dayjs(startDate).year() <= 1970) startDate = undefined;
                    if (dayjs(endDate).year() >= 9999) endDate = undefined;

                    if (startDate && endDate) period = `${startDate} ~ ${endDate}`;
                    else if (startDate) period = `After ${startDate}`;
                    else if (endDate) period = `Before ${endDate}`;

                    return {
                        id: id,
                        title: subtitle,
                        label: period,
                        value: period,
                        type,
                        length: null,
                        essential,
                        placeholder,
                    };
                }
                if (type === "dateTimeRange") {
                    const from = decodedParams[`${id}From`];
                    const to = decodedParams[`${id}To`];
                    let period = "";
                    const startDate = from && dayjs(from).format("YYYY-MM-DD HH:mm");
                    const endDate = to && dayjs(to).format("YYYY-MM-DD HH:mm");

                    if (startDate && endDate) period = `${startDate} ~ ${endDate}`;
                    else if (startDate) period = `After ${startDate}`;
                    else if (endDate) period = `Before ${endDate}`;

                    return {
                        id: id,
                        title: subtitle,
                        label: period,
                        value: period,
                        type,
                        length: null,
                        essential,
                        placeholder,
                    };
                }
                if (type === "dateRange") {
                    const date = value?.split("~");
                    return {
                        id: id,
                        title: subtitle,
                        label: `${date?.[0]} ~ ${date?.[1]}`,
                        value: { start: date?.[0], end: date?.[1] },
                        type,
                        length: null,
                        essential,
                        placeholder,
                    };
                }
                if (type === "tags")
                    return {
                        id: id,
                        title: subtitle,
                        label: value[0],
                        value,
                        type,
                        length: value.length > 1 ? value.length - 1 : null,
                        essential,
                        placeholder,
                    };
                if (type === "attributions")
                    return {
                        id: id,
                        title: subtitle,
                        label: `${value[0].key}:${value[0].value}`,
                        value: value,
                        type,
                        length: value.length > 1 ? value.length - 1 : null,
                        essential,
                        placeholder,
                    };
                if (type === "categories")
                    return {
                        id: id,
                        title: subtitle,
                        label: value?.[0]?.name,
                        value: value,
                        type,
                        length: value.length > 1 ? value.length - 1 : null,
                        essential,
                        placeholder,
                    };
                if (type === "select") {
                    let label = value;
                    if (Array.isArray(value)) {
                        label = value?.[0];
                    }

                    return {
                        id: id,
                        title: subtitle,
                        label,
                        value: value,
                        type,
                        multiple,
                        length: multiple ? value.length - 1 : null,
                        essential,
                        placeholder,
                    };
                }
                if (type === "objectSelect") {
                    let label = "";
                    let length = null;
                    if (Array.isArray(value)) {
                        label = value?.[0]?.label;
                        length = value.length - 1;
                    } else {
                        label = value?.label;
                    }
                    return {
                        id: id,
                        title: subtitle,
                        label,
                        value: value,
                        type,
                        multiple,
                        length,
                        essential,
                        placeholder,
                    };
                }
                if (type === "projects") {
                    let label = Array.isArray(value) ? value?.[0].name : value.name;
                    return {
                        id: id,
                        title: subtitle,
                        label,
                        value: value,
                        type,
                        multiple,
                        length: value.length - 1,
                        essential,
                        placeholder,
                    };
                }
                if (type === "single_project") {
                    return {
                        id: id,
                        title: subtitle,
                        label: value.name,
                        value: value,
                        type,
                        multiple,
                        length: null,
                        essential,
                        placeholder,
                    };
                }
                if (type === "singleCheckbox") {
                    return {
                        id: id,
                        title: subtitle,
                        label: value ? subtitle : null,
                        value: value,
                        type,
                        essential,
                    };
                }
            });
        return paramsForChips;
    };

    const parseSearchValueToSearchParams = (type, value) => {
        const convertDateToString = (dateTime) => {
            return dayjs(dateTime).format("YYYY-MM-DD[T]HH:mm:ss.SSS[Z]");
        };
        const converDateRangeToString = (dateRange) => {
            const { start, end } = dateRange;
            return `${start}~${end}`;
        };

        if (domain === TABLE_LIST_TYPE.ASSET) {
            switch (type) {
                case "assetDateRange":
                    return convertDateToString(value);
                default:
                    return value;
            }
        } else {
            switch (type) {
                case "dateRange":
                    return converDateRangeToString(value);
                default:
                    return value;
            }
        }
    };

    return {
        parseSearchParamsToSearchValue,
        parseSearchValueToSearchParams,
        getSearchFields,
    };
};

export default useSearchFilter;
